/** @jsxImportSource @emotion/react */

import { FC, FocusEvent, useState } from 'react'
import Typography from '@mui/material/Typography'
import { Form as FinalForm } from 'react-final-form'
import Button from '@mui/material/Button'
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-focus'
import { FieldArray } from 'react-final-form-arrays'
import slugify from 'slugify'
import { SubmissionErrors } from 'final-form'
import { useNavigate } from 'react-router-dom'
import { InputField } from 'components/Input'
import { CheckboxField } from 'components/Checkbox'
import { validateRequired, validateRequiredField, validateRequiredHtml } from 'utils/validation'
import { TableOfContentField } from 'components/TableOfContent'
import { EditorField } from 'components/Editor'
import { DropzoneField } from 'components/DropzoneField'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg'
import { ConfirmationModal } from 'components/Modals/ConfirmationModal/ConfirmationModal'
import { setFieldData } from 'utils/setFieldData'
import SelectMembershipType from 'components/SelectMembershipType'
import { SlugInputField } from 'components/SlugInputField/SlugInputField'
import { FormValues } from './types'
import * as classes from './styles'
import { SimilarPosts } from './SimilarPosts'
import { AuthorSearch } from './AuthorSearch'
import { GroupSelect } from './GroupSelect'
import { IconBlock } from './IconBlock'

export const VALIDATE_TABLE_OF_CONTENT_OPTIONS = {
  minCount: 1,
}

const DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

export enum BlogPostType {
  SuccessStory = 'success-story',
  StudyGuide = 'study-guide',
  PatStrategy = 'pat-strategy',
}

type Props = {
  initialValues?: Partial<FormValues>
  platform: Platform
  blogPostType: BlogPostType
  entityId?: string
  isEditing?: boolean
  isDeleting?: boolean
  onSubmit: (values: FormValues) => SubmissionErrors | Promise<SubmissionErrors> | void
  onDelete?: () => Promise<void>
}

export const Form: FC<Props> = ({
  initialValues,
  platform,
  blogPostType,
  entityId,
  isEditing = false,
  isDeleting,
  onSubmit,
  onDelete,
}) => {
  const navigate = useNavigate()
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isSlugEdited, setIsSlugEdited] = useState(isEditing)

  const handleTitleChange =
    (onChange: (name: keyof FormValues, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      if (isSlugEdited) {
        return
      }

      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleSlugBlur =
    (onChange: (name: keyof FormValues, value: string) => void) => (e: FocusEvent<HTMLInputElement>) => {
      onChange('slug', slugify(e.target.value, { lower: true }))
    }

  const handleOpenConfirmationModal = () => {
    setIsConfirmationModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false)
  }

  const handleConfirmDelete = () => {
    onDelete?.()
  }

  const getPrimaryTitle = () => {
    if (blogPostType === BlogPostType.PatStrategy) {
      return isEditing ? 'Edit PAT Strategies' : 'Add PAT Strategies'
    }

    if (blogPostType === BlogPostType.SuccessStory) {
      return isEditing ? 'Edit Success Story' : 'Create Success Story'
    }

    return isEditing ? 'Edit Study General Articles' : 'Add Study General Articles'
  }

  const getSecondaryTitle = () => {
    if (blogPostType === BlogPostType.PatStrategy) {
      return isEditing ? 'Editing Article' : 'Adding Article'
    }

    if (blogPostType === BlogPostType.SuccessStory) {
      return 'Success Story Details'
    }

    return isEditing ? 'Editing Blog' : 'Creating Blog'
  }

  const getNameLabel = () => {
    if (blogPostType === BlogPostType.SuccessStory) {
      return 'Student Name'
    }

    return 'Name'
  }

  const getNamePlaceholder = () => {
    if (blogPostType === BlogPostType.SuccessStory) {
      return 'Enter Student Name'
    }

    return 'Enter Name'
  }

  const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

  return (
    <section css={classes.container}>
      <div css={classes.headerSection}>
        <Typography css={classes.header} variant="title3">
          {getPrimaryTitle()}
        </Typography>
      </div>

      <FinalForm<FormValues>
        initialValues={initialValues}
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={onSubmit}
        render={({ handleSubmit, form, submitting }) => {
          const slugInputProps = {
            name: 'slug',
            validate: validateRequired,
            label: 'Slug',
            placeholder: 'Enter slug',
            fullWidth: true,
            onBlur: handleSlugBlur(form.change),
            onChange: () => setIsSlugEdited(true),
          }

          return (
            <form css={classes.form} onSubmit={handleSubmit}>
              <Typography css={classes.header} variant="title3">
                {getSecondaryTitle()}
              </Typography>

              <div css={classes.hr} />

              <AuthorSearch name="author" validate={validateRequiredField} />

              {blogPostType === BlogPostType.PatStrategy && <GroupSelect />}

              <DropzoneField
                name="coverImage"
                validate={validateRequired}
                dropzoneOptions={DROPZONE_OPTIONS}
                options={{
                  maxFiles: 1,
                  sliceFromEnd: true,
                }}
              />
              <InputField
                name="title"
                validate={validateRequired}
                label="Title"
                placeholder="Enter Title"
                fullWidth
                onBlur={handleTitleChange(form.change)}
              />
              <InputField name="description" label="Description" placeholder="Enter Description" fullWidth />
              <InputField
                name="featuredArticleDescription"
                validate={validateRequired}
                label="Featured Article Description"
                placeholder="Enter Featured Article Description"
                fullWidth
              />
              {isEditing ? <SlugInputField {...slugInputProps} /> : <InputField {...slugInputProps} />}

              {blogPostType === BlogPostType.SuccessStory && (
                <div css={classes.studentWrapper}>
                  <InputField
                    name="fullName"
                    validate={validateRequired}
                    label={getNameLabel()}
                    placeholder={getNamePlaceholder()}
                    fullWidth
                  />
                  <InputField
                    name="score"
                    validate={validateRequired}
                    label="Secondary Line"
                    placeholder="Enter Secondary Line"
                    fullWidth
                  />
                </div>
              )}

              <TableOfContentField name="contentAsHtml" validateOptions={VALIDATE_TABLE_OF_CONTENT_OPTIONS} />
              <EditorField type="article" name="contentAsHtml" validate={validateRequiredHtml} />
              <CheckboxField name="withWhiteImageBG" label="Force white background for images" />

              {[BlogPostType.SuccessStory, BlogPostType.StudyGuide].includes(blogPostType) && (
                <FieldArray name="similarPosts">
                  {({ fields, meta }) => (
                    <SimilarPosts
                      isSuccessStory={blogPostType === BlogPostType.SuccessStory}
                      fields={fields}
                      platform={platform}
                      entityId={entityId}
                      meta={meta}
                    />
                  )}
                </FieldArray>
              )}

              {blogPostType === BlogPostType.PatStrategy && <SelectMembershipType />}

              {blogPostType === BlogPostType.StudyGuide && <IconBlock />}

              <div css={classes.actionButtons}>
                {onDelete && (
                  <Button
                    variant="outlined"
                    color="blue"
                    size="large"
                    type="button"
                    css={[classes.actionButton, classes.deleteButton]}
                    onClick={handleOpenConfirmationModal}
                    disabled={submitting || isDeleting}
                  >
                    <TrashIcon />
                    Delete
                  </Button>
                )}
                <div css={classes.formActionButtonsRight}>
                  <Button
                    variant="outlined"
                    color="blue"
                    size="large"
                    type="button"
                    css={classes.actionButton}
                    disabled={submitting || isDeleting}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="large"
                    color="blue"
                    type="submit"
                    css={classes.actionButton}
                    disabled={submitting || isDeleting}
                  >
                    {isEditing ? 'Save Edits' : 'Create'}
                  </Button>
                </div>
              </div>
            </form>
          )
        }}
      />

      <ConfirmationModal
        open={isConfirmationModalOpen}
        title="Deleting Content"
        subtitle="Are you sure you want to delete success story?"
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmDelete}
        confirmText="Confirm Deleting"
      />
    </section>
  )
}
